import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogProps,
  DialogTitle,
  Stack,
} from 'component-library';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isNullish } from '@/utils/booleans';

const SPECIES_DISPLAY_LIMIT = 3;

export const SpeciesSummary = ({ categoryToSpeciesList }: { categoryToSpeciesList: SpeciesListByCategory[] }) => {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<SpeciesListByCategory | null>(null);
  const selectedGroupLabel = selectedGroup ? selectedGroup.label : '';

  const handleDialogOpenChange = (open: boolean) => {
    if (open === false) {
      setSelectedGroup(null);
    }
  };

  if (categoryToSpeciesList.length === 0) {
    return <span className='typography-body1 text-text-secondary'>{t('global.analysis.noData')}</span>;
  }

  return (
    <Stack spacing={6} data-testid='species-summary'>
      {categoryToSpeciesList.map((item, index) => (
        <Stack key={`${item.category}-${index}`} className='typography-body1 text-text-secondary'>
          <span className='typography-body1Semibold'>
            {item.label} ({item.species.length})
          </span>
          <ul>
            {item.species.slice(0, SPECIES_DISPLAY_LIMIT).map((species, idx) => (
              <li className='list-disc ml-5' key={`${item.category}-${species.name}-${idx}`}>
                <span className='italic'>{species.name}</span> {species.commonName ? `(${species.commonName})` : ''}
              </li>
            ))}
            {item.species.length > SPECIES_DISPLAY_LIMIT && (
              <li className='list-disc ml-5'>
                <Button
                  variant='text'
                  className='p-0 underline hover:bg-transparent hover:text-primary-light'
                  onClick={() => setSelectedGroup(item)}
                >
                  {t('global.analysis.seeMore')}
                </Button>
              </li>
            )}
          </ul>
        </Stack>
      ))}
      {selectedGroup && (
        <SpeciesDialog
          open
          onOpenChange={handleDialogOpenChange}
          data={selectedGroup}
          speciesCategoryLabel={selectedGroupLabel}
        />
      )}
    </Stack>
  );
};

export type SpeciesListByCategory = {
  label: string;
  category: string;
  species: {
    name: string;
    count?: number;
    kingdom?: string | undefined;
    commonName?: string | undefined;
  }[];
  species_category?: string | undefined;
};

const SpeciesDialog: FC<DialogProps & { speciesCategoryLabel: string; data: SpeciesListByCategory }> = ({
  speciesCategoryLabel,
  data,
  ...props
}) => {
  const { t } = useTranslation();

  return (
    <Dialog {...props}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${speciesCategoryLabel} ${t('global.analysis.species')}`}</DialogTitle>
          <DialogBody className='max-h-[60vh] overflow-y-auto'>
            <ul>
              {data.species.map((species) => (
                <li className='list-disc ml-5 my-2' key={species.name}>
                  <span className='italic'>{species.name}</span> {species.commonName ? `(${species.commonName})` : ''}
                  {!isNullish(species.count) ? `(${species.count})` : ''}
                </li>
              ))}
            </ul>
          </DialogBody>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
